import { noteActivity } from "./session-activity";

/**
 * Thin API client seam (DE-114 /api/v1).
 *
 * The session is the accounts-rbac module's HttpOnly cookie (DO-013, NF-008):
 * the SPA holds no token, sends no `Authorization` header and never sees the
 * session secret. Every request is same-origin with credentials, and the
 * cookie (SameSite=Strict) rides along by itself.
 *
 * Two request kinds, because the server counts them differently (NF-008,
 * module 0.3.0):
 *
 *   - a request the PERSON causes resets the idle limit - it is noted as
 *     activity here, so the expiry warning knows the idle edge moved;
 *   - a request the SURFACE makes on its own (polling, the session check)
 *     carries `X-Background: 1` and reads the session without resetting it.
 *     Without the header a page left open would never go idle.
 *
 * A 401 on a request that needed a session means the session is gone (expired,
 * idle, revoked, account retired). Listeners registered with `onSessionEnded`
 * learn of it - the auth context drops to the login. Requests of the login
 * screens themselves pass `anonymous: true`: a refused password is a 401 too,
 * and it ends nothing.
 *
 * Types come from the generated contract (ADR-S1-005) where the API has one;
 * the module's own routes are hand-typed in features/access/api.ts until the
 * contract carries them.
 */
const BASE = "/api/v1";

/** The header a request carries when the surface, not the person, made it. */
export const BACKGROUND_HEADER = "X-Background";

export interface ApiError {
  // RFC 7807 problem+json body (DE-118). Machine-checked via type/status.
  type: string;
  title: string;
  status: number;
  detail: string;
  // Structured field errors on validation problems (e.g. the failing filename
  // on an NF-005 upload rejection: "{filename}: {reason}").
  errors?: Array<{ path: string; message: string }>;
}

export interface RequestOptions {
  /** Made by the surface on its own (a poll); does not reset the idle limit. */
  background?: boolean;
  /** A login-screen request: its 401 is an answer, not the end of a session. */
  anonymous?: boolean;
}

/**
 * The RequestInit every call goes out with. JSON is the default only for a
 * request that HAS a body - a bodyless write announcing JSON was D-002 -, and
 * never for multipart (the browser sets the boundary).
 */
export function requestInit(init: RequestInit | undefined, options: RequestOptions = {}): RequestInit {
  const headers = new Headers(init?.headers);
  const hasBody = init?.body !== undefined && init.body !== null;
  const multipart = init?.body instanceof FormData;
  if (hasBody && !multipart && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  if (options.background) headers.set(BACKGROUND_HEADER, "1");
  return { ...init, credentials: "include", headers };
}

const sessionEndedListeners = new Set<() => void>();

/** Called once per request that found its session gone; returns the unsubscribe. */
export function onSessionEnded(listener: () => void): () => void {
  sessionEndedListeners.add(listener);
  return () => sessionEndedListeners.delete(listener);
}

/**
 * Report a session found gone by other means than a 401 - the session check
 * answering `authenticated: false` once the idle edge has passed.
 */
export function endSession(): void {
  for (const listener of sessionEndedListeners) listener();
}

/** The error every failed request throws: message, status, and the raw body. */
export type ApiRequestError = Error & {
  status: number;
  /** The body when it parsed as JSON - RFC 7807 or the module's `{ error }`. */
  problem: ApiError | undefined;
  body: Record<string, unknown>;
};

/**
 * The German sentence a refusal carries: RFC 7807 `detail`, or the module's
 * `error`. The status text only when the server said nothing.
 */
export function messageOf(body: Record<string, unknown>, fallback: string): string {
  if (typeof body.detail === "string" && body.detail) return body.detail;
  if (typeof body.error === "string" && body.error) return body.error;
  return fallback;
}

/**
 * apiFetch minus the JSON body parse: returns the raw Response for callers
 * that stream/blob the payload (attachment download, ADR-API-06).
 */
export async function apiFetchRaw(path: string, init?: RequestInit, options: RequestOptions = {}): Promise<Response> {
  const res = await fetch(`${BASE}${path}`, requestInit(init, options));
  // The server reset the idle limit with this request, refused or not (NF-008).
  if (!options.background && res.status !== 401) noteActivity(Date.now());

  if (!res.ok) {
    const parsed: unknown = await res.json().catch(() => undefined);
    const body = parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
    if (res.status === 401 && !options.anonymous) endSession();
    const error: ApiRequestError = Object.assign(new Error(messageOf(body, res.statusText)), {
      status: res.status,
      problem: parsed as ApiError | undefined,
      body,
    });
    throw error;
  }
  return res;
}

export async function apiFetch<T>(path: string, init?: RequestInit, options: RequestOptions = {}): Promise<T> {
  const res = await apiFetchRaw(path, init, options);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}
